import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { useHeartbeat } from '../../auth/hooks/useHeartbeat';

/**
 * Tracks user activity and enforces idle-redirect using VITE_IDLE_REDIRECT_MINUTES.
 * Also triggers periodic heartbeat to keep sliding session alive while active.
 */
const IdleTracker: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { ping } = useHeartbeat();
  const timer = useRef<number | null>(null);
  const lastActive = useRef<number>(Date.now());

  const idleMinutes = parseInt(import.meta.env.VITE_IDLE_REDIRECT_MINUTES || '15', 10);
  const warnBeforeMs = parseInt(import.meta.env.VITE_IDLE_WARN_BEFORE_MS || '0', 10);

  useEffect(() => {
    const onAny = () => { lastActive.current = Date.now(); };
    const evs = ['mousemove','mousedown','keydown','touchstart','scroll'];
    evs.forEach(e => window.addEventListener(e, onAny, { passive: true }));

    const loop = () => {
      const now = Date.now();
      const diff = now - lastActive.current;
      // Send heartbeat every 5 minutes when authenticated
      if (user && diff < 5 * 60 * 1000) {
        ping();
      }
      // Pre-warning (no-op UI; reserved)
      if (warnBeforeMs > 0 && diff > (idleMinutes*60*1000 - warnBeforeMs)) {
        // Could open a toast or modal here
      }
      // Idle redirect
      if (user && diff >= idleMinutes * 60 * 1000) {
        logout();
        navigate('/login', { replace: true });
      }
      timer.current = window.setTimeout(loop, 60 * 1000);
    };
    timer.current = window.setTimeout(loop, 60 * 1000);

    return () => {
      evs.forEach(e => window.removeEventListener(e, onAny));
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [user, idleMinutes, warnBeforeMs, navigate, ping, logout]);

  return null;
};

export default IdleTracker;
